import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { registerPopupHost, unregisterPopupHost } from '../../utils/popup';

const VARIANT_CONFIG = {
    info: {
        badge: 'bg-primary-50 text-primary-700 border-primary-200',
        iconBg: 'bg-primary-100',
        iconColor: 'text-primary-600',
        accent: 'border-l-primary-500',
        msgBg: 'bg-primary-50/40 border-primary-100',
        label: 'Information',
    },
    success: {
        badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        accent: 'border-l-emerald-500',
        msgBg: 'bg-emerald-50/40 border-emerald-100',
        label: 'Success',
    },
    warning: {
        badge: 'bg-amber-50 text-amber-700 border-amber-200',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        accent: 'border-l-amber-500',
        msgBg: 'bg-amber-50/40 border-amber-100',
        label: 'Please Review',
    },
    error: {
        badge: 'bg-red-50 text-red-700 border-red-200',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        accent: 'border-l-red-500',
        msgBg: 'bg-red-50/40 border-red-100',
        label: 'Attention Needed',
    },
};

function PopupIcon({ variant, cfg }) {
    const cls = `h-5 w-5 ${cfg.iconColor}`;
    switch (variant) {
        case 'success':
            return (
                <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75 11.25 15 15 9.75m6 2.25a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
            );
        case 'warning':
            return (
                <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m0 3.75h.008v.008H12v-.008ZM10.34 3.94 1.82 18.06A1.5 1.5 0 0 0 3.1 20.25h17.8a1.5 1.5 0 0 0 1.28-2.19L13.66 3.94a1.5 1.5 0 0 0-2.56 0Z" />
                </svg>
            );
        case 'error':
            return (
                <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 9.75 14.25 14.25m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
            );
        default:
            return (
                <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.25 11.25 12 11.25v5.25H12.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3h.008v.008H12V9Z" />
                </svg>
            );
    }
}

function splitLines(message) {
    return String(message || '')
        .split(/\n+/)
        .map((item) => item.trim())
        .filter(Boolean);
}

function renderMessageLine(line, key) {
    if (/^[-•]/.test(line)) {
        return (
            <div key={key} className="flex items-start gap-2">
                <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400" />
                <span className="text-sm leading-6 text-slate-600">{line.replace(/^[-•]\s*/, '')}</span>
            </div>
        );
    }
    return (
        <p key={key} className="text-sm leading-6 text-slate-600">
            {line}
        </p>
    );
}

export default function PopupHost() {
    const [queue, setQueue] = useState([]);
    const current = queue[0] || null;

    const open = useCallback((popup) => {
        return new Promise((resolve) => {
            setQueue((prev) => [...prev, { ...popup, resolve, id: `${Date.now()}-${Math.random()}` }]);
        });
    }, []);

    useEffect(() => {
        registerPopupHost({ open });
        return () => unregisterPopupHost();
    }, [open]);

    useEffect(() => {
        if (!current) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                closeCurrent(current.kind === 'confirm' ? false : true);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [current]);

    const cfg = useMemo(() => VARIANT_CONFIG[current?.variant || 'info'], [current]);

    const closeCurrent = (result) => {
        if (!current) return;
        current.resolve(result);
        setQueue((prev) => prev.slice(1));
    };

    if (!current || typeof document === 'undefined') return null;

    const messageLines = splitLines(current.message);
    const isConfirm = current.kind === 'confirm';
    const isDestructive = /(delete|remove|reset|archive)/i.test(
        `${current.title || ''} ${current.message || ''}`
    );

    return createPortal(
        <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
                onClick={() => closeCurrent(isConfirm ? false : true)}
            />

            {/* Dialog card */}
            <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden">

                {/* Top accent bar — coloured by variant */}
                <div className={`h-1 w-full bg-gradient-to-r ${current.variant === 'success' ? 'from-emerald-400 to-green-500' :
                        current.variant === 'warning' ? 'from-amber-400 to-orange-500' :
                            current.variant === 'error' ? 'from-red-400 to-rose-500' :
                                'from-primary-500 to-purple-600'
                    }`} />

                <div className="px-6 pt-5 pb-6">

                    {/* Header row */}
                    <div className="flex items-start gap-3.5">
                        {/* Icon pill */}
                        <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${cfg.iconBg}`}>
                            <PopupIcon variant={current.variant} cfg={cfg} />
                        </div>

                        {/* Title + badge */}
                        <div className="flex-1 min-w-0 pt-0.5">
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${cfg.badge}`}>
                                {isConfirm ? 'Confirm' : cfg.label}
                            </span>
                            <h3 className="mt-1 text-[15px] font-semibold text-slate-900 leading-snug">
                                {current.title}
                            </h3>
                        </div>

                        {/* Close X */}
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={() => closeCurrent(isConfirm ? false : true)}
                            className="flex-shrink-0 mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                        >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18 18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Message body */}
                    {(messageLines.length > 0 || current.details) && (
                        <div className={`mt-4 rounded-xl border px-4 py-3 ${cfg.msgBg}`}>
                            {messageLines.length > 0 && (
                                <div className="space-y-1.5">
                                    {messageLines.map((line, i) => renderMessageLine(line, `${current.id}-${i}`))}
                                </div>
                            )}
                            {current.details && (
                                <p className="mt-2 text-xs text-slate-400 border-t border-slate-100 pt-2">{current.details}</p>
                            )}
                        </div>
                    )}

                    {/* Action buttons */}
                    <div className="mt-5 flex items-center justify-end gap-2.5">
                        {isConfirm && (
                            <button
                                type="button"
                                onClick={() => closeCurrent(false)}
                                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-200"
                            >
                                {current.cancelLabel || 'Cancel'}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => closeCurrent(true)}
                            className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 ${isConfirm && isDestructive
                                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-300'
                                    : 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-300'
                                }`}
                        >
                            {current.confirmLabel || (isConfirm ? 'Confirm' : 'Got it')}
                        </button>
                    </div>

                    {/* Subtle esc hint */}
                    <p className="mt-3 text-center text-[10px] text-slate-300 tracking-wide">
                        Press Esc to {isConfirm ? 'cancel' : 'close'}
                    </p>
                </div>
            </div>
        </div>,
        document.body,
    );
}
